import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';

import IconButton from 'components/common/IconButton';
import Menu, { MenuItem } from 'components/common/Menu';
import DeleteMenu from 'components/common/DeleteMenu';
import IconMoreVert from 'components/icons/IconMoreVert';
import NoteCategoryCreateEditDialog from 'components/settings/NoteCategoryCreateEditDialog';
import Link from 'components/common/Link';
import IconArrowLeft from 'components/icons/IconArrowLeft';
import IconCalendarStart from 'components/icons/IconCalendarStart';
import Typography from '@material-ui/core/Typography';
import Tooltip from 'components/common/Tooltip';

import splitLongWords from 'utils/splitLongWords';

import { deleteNoteCategory } from 'redux/modules/noteCategories';

const styles = theme => ({
  root: {
    display: 'flex',
    width: '100%',
    flexWrap: 'nowrap',
    '&:last-child td': {
      borderBottom: 'none',
    },
    '&:hover $buttonMenu button': {
      opacity: 1,
    },
  },
  iconMoreVert: {
    width: 24,
    height: 24,
  },
  buttonCell: {
    display: 'block',
    textAlign: 'right',
    padding: 0,
    '&:last-child': {
      padding: 0,
    },
  },
  cellName: {
    display: 'flex',
    alignItems: 'center',
    maxWidth: '70%',
    flexBasis: '70%',
    paddingRight: 0,
    paddingTop: 8,
    paddingBottom: 8,
  },
  buttonMenu: {
    marginLeft: -8,
    '& button': {
      opacity: 0,
      transition: theme.transitions.create(['opacity'], {
        duration: theme.transitions.duration.short,
      }),
      '&:focus': {
        opacity: 1,
      },
    },
  },
  buttonMenuActive: {
    '& button': {
      opacity: 1,
    },
  },
  iconCalendarStart: {
    width: 18,
    height: 18,
    marginRight: 4,
    marginBottom: -4,
    color: theme.palette.states.delivery,
  },
  iconArrowLeft: {
    width: 10,
    height: 10,
    margin: '-2px 10px 0 10px',
    verticalAlign: 'middle',
    transform: 'rotate(180deg)',
    color: 'inherit',
  },
  nameSuffix: {
    display: 'inline-block',
    whiteSpace: 'nowrap',
  },
});

class NoteCategoriesTableRow extends Component {
  constructor(props) {
    super(props);

    this.state = {
      anchorEl: null,
      deleteMenuOpen: false,
      editDialogOpen: false,
    };

    this.onMenuButtonClick = this.onMenuButtonClick.bind(this);
    this.onMenuClose = this.onMenuClose.bind(this);
    this.onDeleteMenuItemClick = this.onDeleteMenuItemClick.bind(this);
    this.onDeleteCancel = this.onDeleteCancel.bind(this);
    this.onDeleteSubmit = this.onDeleteSubmit.bind(this);
    this.onEditMenuItemClick = this.onEditMenuItemClick.bind(this);
    this.onEditDialogClose = this.onEditDialogClose.bind(this);
  }

  onMenuButtonClick(event) {
    this.setState({
      anchorEl: event.currentTarget,
      deleteMenuOpen: false,
    });
  }

  onMenuClose() {
    this.setState({ anchorEl: null });
  }

  onEditMenuItemClick() {
    this.setState({
      anchorEl: null,
      editDialogOpen: true,
    });
  }

  onDeleteMenuItemClick() {
    this.setState({ deleteMenuOpen: true });
  }

  onDeleteCancel() {
    this.setState({ anchorEl: null });
  }

  onDeleteSubmit() {
    const { ID, Name, actions, onNoteCategoryDeleteFail } = this.props;
    actions.deleteNoteCategory({
      values: { ID },
      showNotification: false,
      reject: (error) => {
        onNoteCategoryDeleteFail({
          error: error.description,
          reassignNoteCategory: {
            ID,
            Name,
          },
        })
      },
    });
    this.setState({
      deleteMenuOpen: false,
      anchorEl: null,
    });
  }

  onEditDialogClose() {
    this.setState({ editDialogOpen: false });
  }

  render() {
    const { ID, account, Name, Description, classes } = this.props;
    const { anchorEl, deleteMenuOpen, editDialogOpen } = this.state;
    const open = Boolean(anchorEl);

    return (
      <TableRow className={classes.root}>
        <TableCell className={classes.cellName}>
          <Link
            breakWord
            {...this.props['Is Default'] ? {
              color: 'secondary',
            } : {}}
            onClick={this.onEditMenuItemClick}
          >
            {splitLongWords(Name)}
            {this.props['Is Delivery'] &&
              <div className={classes.nameSuffix}>
                <IconArrowLeft className={classes.iconArrowLeft} />
                <Tooltip
                  content={
                    <Typography>
                      Printed in Delivery Instructions
                    </Typography>
                  }
                  placement="top"
                >
                  <IconCalendarStart className={classes.iconCalendarStart} />
                </Tooltip>
              </div>
            }
          </Link>
        </TableCell>
        <TableCell className={classes.buttonCell}>
          <IconButton
            className={classNames(classes.buttonMenu, {
              [classes.buttonMenuActive]: open,
            })}
            aria-label="More"
            aria-owns={open ? `${ID}-workflow-status-menu` : null}
            aria-haspopup="true"
            onClick={this.onMenuButtonClick}
            tooltipContent="Open Menu"
          >
            <IconMoreVert className={classes.iconMoreVert} />
          </IconButton>
          <Menu
            id={`${ID}-workflow-status-menu`}
            anchorEl={anchorEl}
            open={open}
            onClose={this.onMenuClose}
            list={!deleteMenuOpen}
            maxHeight={130}
          >
            {deleteMenuOpen ? (
              <DeleteMenu
                text="Are you sure you want to delete this workflow status?"
                onCancel={this.onDeleteCancel}
                onSubmit={this.onDeleteSubmit}
              />
            ) : (
              <Fragment>
                <MenuItem onClick={this.onEditMenuItemClick}>
                  Edit
                </MenuItem>
                <MenuItem
                  onClick={this.onDeleteMenuItemClick}
                  color="warning"
                  disabled={account.Roles.filter(item =>
                    item.Name === 'Master Administrator' || item.Name === 'Administrator' || item.Name === 'Manager').length === 0}
                >
                  Delete
                </MenuItem>
              </Fragment>
            )}
          </Menu>
        </TableCell>
        <NoteCategoryCreateEditDialog
          form={`noteCategoryEdit-${ID}`}
          open={editDialogOpen}
          ID={ID}
          initialValues={{
            Name,
            'Is Delivery': this.props['Is Delivery'],
            'Workflow State ID': this.props['Workflow State'].ID,
            'Workflow State Name': this.props['Workflow State'].Name,
            'Selected Workflow State Name': this.props['Workflow State'].Name,
            'Is Active': this.props['Is Active'],
          }}
          onClose={this.onEditDialogClose}
          onSubmitSuccess={this.onEditDialogClose}
        />
      </TableRow>
    );
  }
}

NoteCategoriesTableRow.propTypes = {
  ID: PropTypes.number.isRequired,
  Name: PropTypes.string.isRequired,
  'Is Delivery': PropTypes.bool,
  actions: PropTypes.shape({
    deleteNoteCategory: PropTypes.func.isRequired,
  }).isRequired,
  account: PropTypes.shape({
    Roles: PropTypes.arrayOf(PropTypes.shape({
      Name: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    iconMoreVert: PropTypes.string.isRequired,
    buttonCell: PropTypes.string.isRequired,
  }).isRequired,
};

NoteCategoriesTableRow.defaultProps = {
  'Is Delivery': false,
};

const mapStateToProps = store => ({
  account: store.account,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ deleteNoteCategory }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles)(NoteCategoriesTableRow),
);



// WEBPACK FOOTER //
// ./src/components/settings/NoteCategoriesTable/NoteCategoriesTableRow.js